// Partners Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { PartnerForm } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => { fetchPartners(); }, []);

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/partners/all`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPartners(data || []);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partenaire ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/partners/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/partners/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchPartners();
    } catch (err) {
      console.error('Error saving partner:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/partners`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchPartners();
    } catch (err) {
      console.error('Error adding partner:', err);
    }
  };

  const handleReorder = async (index, direction) => {
    const newPartners = [...partners];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newPartners.length) return;
    
    // Swap orders
    const currentOrder = newPartners[index].order;
    const targetOrder = newPartners[targetIndex].order;
    
    newPartners[index].order = targetOrder;
    newPartners[targetIndex].order = currentOrder;
    
    // Swap positions
    [newPartners[index], newPartners[targetIndex]] = [newPartners[targetIndex], newPartners[index]];
    setPartners(newPartners);
    
    // Update in backend
    try {
      await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/partners/${newPartners[index].id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(newPartners[index])
        }),
        fetch(`${BACKEND_URL}/api/admin/partners/${newPartners[targetIndex].id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(newPartners[targetIndex])
        })
      ]);
    } catch (err) { console.error('Error reordering:', err); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Partenaires</h2>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ name: '', country: '', logo: '', website: '', description_en: '', description_fr: '', description_ar: '', full_description_en: '', full_description_fr: '', full_description_ar: '', images: [], video_id: '', order: partners.length, is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouveau Partenaire</h3>
          <PartnerForm form={editForm} setForm={setEditForm} onSave={handleAdd} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="space-y-4">
        {partners.map((partner, index) => (
          <div key={partner.id} className="border border-slate-200 rounded-lg p-4 bg-white">
            {editingId === partner.id ? (
              <PartnerForm form={editForm} setForm={setEditForm} onSave={() => handleSave(partner.id)} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="flex items-center gap-4">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleReorder(index, 'up')}
                    disabled={index === 0}
                    className={`p-1 rounded ${index === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleReorder(index, 'down')}
                    disabled={index === partners.length - 1}
                    className={`p-1 rounded ${index === partners.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                
                <img src={partner.logo} alt={partner.name} className="w-20 h-12 object-contain bg-white rounded flex-shrink-0" />
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-800 truncate">{partner.name}</h3>
                    <span className="text-sm text-slate-500 flex-shrink-0">({partner.country})</span>
                    {!partner.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs flex-shrink-0">Inactif</span>}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{partner.description_fr}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingId(partner.id); setEditForm(partner); }}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(partner.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {partners.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucun partenaire.</div>}
    </div>
  );
};

export default PartnersManager;
